import type { ApiErrorBody } from '@/domain/types'

// ---------------------------------------------------------------------------
// ApiError — typed error carrying the API's error envelope
// ---------------------------------------------------------------------------

export class ApiError extends Error {
  constructor(
    public readonly code: string,
    message: string,
    public readonly traceId: string,
    public readonly status: number,
    public readonly details?: unknown,
  ) {
    super(message)
    this.name = 'ApiError'
  }
}

// ---------------------------------------------------------------------------
// apiFetch — the single exit point for all API calls
// ---------------------------------------------------------------------------

// Checked once, when the app loads, so a missing key fails naming itself
// rather than as "cannot read properties of undefined" on the first request.
if (!import.meta.env.VITE_API_BASE_URL) {
  throw new Error(
    'VITE_API_BASE_URL is not set. Add it to web/.env; web/.env.example lists the keys.',
  )
}

let writes = 0

/**
 * How many non-GET requests have completed. useApiQuery keeps recent GET
 * results for a few seconds; any write may change what a GET returns, so a
 * result fetched before the latest write is not reused.
 */
export function writeCount(): number {
  return writes
}

/**
 * Builds the full URL from VITE_API_BASE_URL, attaches the caller's identity
 * headers, and parses the response. Throws ApiError for non-2xx responses.
 *
 * @param path        Path including leading slash, e.g. `/api/lookup/stages`
 * @param getHeaders  Called on every request; a token may need refreshing
 * @param init        Standard RequestInit (method, body, etc.)
 */
export async function apiFetch<T>(
  path: string,
  getHeaders: () => Promise<Record<string, string>>,
  init?: RequestInit,
): Promise<T> {
  const base = import.meta.env.VITE_API_BASE_URL.replace(/\/$/, '')
  const url = `${base}${path}`

  const auth = await getHeaders()

  const response = await fetch(url, {
    ...init,
    headers: {
      // Only with a body: Fastify refuses a JSON content-type on an empty one,
      // which a bodiless PUT or DELETE would otherwise send.
      ...(init?.body === undefined ? {} : { 'Content-Type': 'application/json' }),
      ...auth,
      ...init?.headers,
    },
  })
  // Counted even when it failed: a rejected write may still have changed something.
  if ((init?.method ?? 'GET').toUpperCase() !== 'GET') writes += 1

  if (!response.ok) {
    let body: ApiErrorBody
    try {
      body = (await response.json()) as ApiErrorBody
    } catch {
      // Non-JSON error body — synthesise a minimal envelope
      body = {
        code: 'UNKNOWN_ERROR',
        message: `HTTP ${response.status}`,
        traceId: '',
      }
    }
    throw new ApiError(body.code, body.message, body.traceId, response.status, body.details)
  }

  // 204 No Content has no body. The API uses this for DELETE; cast
  // through unknown to satisfy the generic without pretending we have
  // a value.
  if (response.status === 204) {
    return undefined as unknown as T
  }

  return response.json() as Promise<T>
}
